/* __next_internal_client_entry_do_not_use__ ButtonWrapper,Carousel,PreviousButton,NextButton,CarouselHorizontalScrollCarouselItem auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { Grid } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import chunk from "lodash/chunk";
import size from "lodash/size";
import times from "lodash/times";
import * as React from "react";
import { useState } from "react";
import SwipeableViews from "react-swipeable-views";
import { virtualize } from "react-swipeable-views-utils";
import styled, { css } from "styled-components";
import { HorizontalScrollCarousel } from "@faire/retailer/components/Carousel/HorizontalScrollCarousel";
import { CarouselDots } from "@faire/retailer/components/ImageCarousel/CarouselDots";
import { AccessibleOutlineCSS } from "../AccessibleOutlineCSS";
import { maybeAddCTATile } from "./CTATile";
var VirtualizedSwipeableViews = virtualize(SwipeableViews);
var CarouselBase = function(props) {
    var _props_padItems = props.padItems, padItems = _props_padItems === void 0 ? false : _props_padItems, peekNext = props.peekNext, noWrap = props.noWrap, _props_itemGap = props.itemGap, itemGap = _props_itemGap === void 0 ? 20 : _props_itemGap, _props_peekGapPercent = props.peekGapPercent, peekGapPercent = _props_peekGapPercent === void 0 ? 10 : _props_peekGapPercent, enableMouseEvents = props.enableMouseEvents, useHorizontalScrollCarousel = props.useHorizontalScrollCarousel, swipeableLeftPadding = props.swipeableLeftPadding, disableScrollWhenSingleSlide = props.disableScrollWhenSingleSlide, alwaysIncludePeek = props.alwaysIncludePeek, onVisibile = props.onVisibile;
    var viewRef = useDoActionWhenVisible(onVisibile);
    var _useState = _sliced_to_array(useState(0), 2), slideIndex = _useState[0], setSlideIndex = _useState[1];
    var _useState1 = _sliced_to_array(useState(noWrap || isPrerender() ? 0 : 1), 2), overscanBefore = _useState1[0], setOverscanBefore = _useState1[1];
    var _useState2 = _sliced_to_array(useState(1), 2), overscanAfter = _useState2[0], setOverscanAfter = _useState2[1];
    var items = maybeAddCTATile(props.items, props.ctaTileData, props.itemsPerSlide);
    var itemsPerSlide = padItems ? props.itemsPerSlide : Math.min(props.itemsPerSlide, items === null || items === void 0 ? void 0 : items.length);
    var slideCount = Math.ceil(size(items) / itemsPerSlide);
    var updateOverscanNext = function() {
        if (slideIndex >= slideCount - 2) {
            setOverscanAfter(0);
        } else {
            setOverscanAfter(1);
        }
        setOverscanBefore(1);
    };
    var updateOverscanBefore = function() {
        if (slideIndex <= 1) {
            setOverscanBefore(0);
        } else {
            setOverscanBefore(1);
        }
        setOverscanAfter(1);
    };
    var handleChangeSlideIndex = function(inSlideIndex) {
        if (inSlideIndex > slideIndex) {
            showNextTiles();
        } else if (inSlideIndex < slideIndex) {
            showPreviousTiles();
        }
    };
    var handleChangeSlideIndexFromDot = function(inSlideIndex) {
        // The carousel tracks indices in a wrapping carousel as tiles extending
        // off to infinity, i.e. even if there are 3 pages,
        // you can have an index of 5 or 500 if you keep clicking next.
        var loopedTargetPosition = positiveMod(inSlideIndex, slideCount);
        var loopedCurrentPosition = positiveMod(slideIndex, slideCount);
        if (loopedTargetPosition === loopedCurrentPosition) {
            return;
        }
        if (props.noWrap) {
            setSlideIndex(loopedTargetPosition);
            trackScrollEvent(loopedTargetPosition < loopedCurrentPosition ? "previous" : "next", loopedTargetPosition);
        } else {
            // When the user clicks on a dot below the carousel, we
            // want to scroll in the direction that the user expects -- so if the dot
            // they clicked on is to the left of the active dot, we should scroll to
            // the nearest index that is to the left of the active index.
            //
            // This logic selects that index.
            var shouldScrollLeft = loopedTargetPosition < loopedCurrentPosition;
            var leftEdge = roundDown(slideIndex, slideCount);
            var targetIndex = leftEdge + loopedTargetPosition;
            if (shouldScrollLeft && targetIndex > slideIndex) {
                targetIndex -= slideCount;
            } else if (!shouldScrollLeft && targetIndex < slideIndex) {
                targetIndex += slideCount;
            }
            setSlideIndex(targetIndex);
            trackScrollEvent(shouldScrollLeft ? "previous" : "next", loopedTargetPosition);
        }
    };
    var showNextTiles = function() {
        var targetIndex = slideIndex + 1;
        if (props.noWrap && targetIndex >= slideCount) {
            return;
        }
        setSlideIndex(targetIndex);
        if (targetIndex === slideCount - 1) {
            trackEvent("/".concat(props.name, "/carousel/lastitems"), true);
        }
        if (noWrap) {
            updateOverscanNext();
        }
        trackScrollEvent("next", targetIndex);
    };
    var showPreviousTiles = function() {
        var targetIndex = slideIndex - 1;
        if (props.noWrap && targetIndex < 0) {
            return;
        }
        setSlideIndex(targetIndex);
        if (targetIndex === slideCount - 1) {
            trackEvent("/".concat(props.name, "/carousel/lastitems"), true);
        }
        if (noWrap) {
            updateOverscanBefore();
        }
        trackScrollEvent("previous", targetIndex);
    };
    var trackScrollEvent = function(direction, targetIndex) {
        if (props.onScroll) {
            props.onScroll(direction, targetIndex % slideCount, targetIndex);
        } else {
            trackEvent("/".concat(props.name, "/carousel/").concat(direction), true);
        }
    };
    var computeSlideItems = function(inSlideIndex) {
        if (slideCount === 0) {
            return [];
        }
        var start = inSlideIndex * itemsPerSlide;
        var itemsThisSlide = times(itemsPerSlide).map(function(i) {
            return getVirtualItem(start + i);
        });
        if (props.noWrap && inSlideIndex + 1 === slideCount) {
            itemsThisSlide = itemsThisSlide.slice(0, items.length - props.itemsPerSlide * inSlideIndex);
        }
        return itemsThisSlide;
    };
    var shouldPadItem = function(tileIndex) {
        return padItems && props.itemsPerSlide > items.length && tileIndex >= items.length;
    };
    var renderItem = function(item, tileIndex) {
        if (shouldPadItem(tileIndex)) {
            return /*#__PURE__*/ _jsx(PaddedItem, {}, "carousel-padded-item-".concat(tileIndex));
        }
        if (/*#__PURE__*/ React.isValidElement(item)) {
            return item;
        }
        return props.renderItem(item, tileIndex, slideIndex);
    };
    var slideRenderer = function(param) {
        var key = param.key, index = param.index;
        var slideItems = computeSlideItems(index);
        /* don't render slides that are not in the view with SSR */ if (props.isSSR && index !== slideIndex) {
            return /*#__PURE__*/ _jsx(React.Fragment, {}, key);
        }
        var _props_peekPadding;
        return /*#__PURE__*/ _jsx(StyledGrid, {
            gap: itemGap,
            columns: itemsPerSlide,
            $padding: props.peekNext ? (_props_peekPadding = props.peekPadding) !== null && _props_peekPadding !== void 0 ? _props_peekPadding : "0 ".concat((itemGap || 0) / 2, "px") : undefined,
            children: slideItems.map(renderItem)
        }, key);
    };
    var getVirtualItem = function(virtualIndex) {
        var index = virtualIndex % size(items);
        index = index >= 0 ? index : size(items) + index;
        var _items_index;
        return (_items_index = items[index]) !== null && _items_index !== void 0 ? _items_index : /*#__PURE__*/ _jsx(_Fragment, {});
    };
    var renderNonVirtualizedSlides = function() {
        var chunks = chunk(items, itemsPerSlide);
        return chunks.map(function(slideChunk, chunkIndex) {
            /* don't render slides that are not in the view with SSR */ if (props.isSSR && chunkIndex !== slideIndex) {
                return /*#__PURE__*/ _jsx(_Fragment, {});
            }
            var padding = props.removePaddingFromFirstAndLastItems ? "0" : "0 ".concat((itemGap || 0) / 2, "px");
            return /*#__PURE__*/ _jsx(StyledGrid, {
                columns: itemsPerSlide,
                gap: itemGap,
                $padding: padding,
                children: slideChunk.map(function(item, index) {
                    return renderItem(item, index);
                })
            }, chunkIndex);
        });
    };
    var renderCarousel = function() {
        var swipeablePadding = "0 ".concat(peekGapPercent, "% 0 ").concat(swipeableLeftPadding !== null && swipeableLeftPadding !== void 0 ? swipeableLeftPadding : itemGap / 2 - 1, "px");
        if (peekNext && noWrap) {
            if (props.isMobile || useHorizontalScrollCarousel) {
                return /*#__PURE__*/ _jsx(HorizontalScrollCarousel, {
                    dataTestId: "horizontal-scroll-carousel",
                    items: props.items,
                    itemGap: itemGap,
                    itemsPerSlide: itemsPerSlide,
                    renderItem: props.renderItem,
                    peekGapPercent: peekGapPercent,
                    lateralPadding: props.lateralPadding,
                    onScroll: props.onMobileScroll,
                    onScrollEnd: props.onMobileScrollEnd,
                    ctaTileData: props.ctaTileData
                });
            }
            return /*#__PURE__*/ _jsx(StyledSwipeableViews, {
                index: slideIndex,
                onChangeIndex: handleChangeSlideIndex,
                $slideCount: slideCount,
                $swipeablePadding: swipeablePadding,
                $alwaysIncludePeek: alwaysIncludePeek,
                enableMouseEvents: enableMouseEvents,
                containerStyle: {
                    transition: "transform 0.35s cubic-bezier(0.15, 0.3, 0.25, 1) 0s"
                },
                children: renderNonVirtualizedSlides()
            });
        }
        var paddingForVirtualized = peekNext && itemsPerSlide < items.length ? swipeablePadding : undefined;
        return /*#__PURE__*/ _jsx(StyledVirtualizedSwipeableViews, {
            $swipeablePadding: paddingForVirtualized,
            index: slideIndex,
            onChangeIndex: handleChangeSlideIndex,
            slideRenderer: slideRenderer,
            enableMouseEvents: enableMouseEvents,
            disabled: disableScrollWhenSingleSlide && slideCount <= 1,
            overscanSlideAfter: isPrerender() ? slideCount : overscanAfter,
            overscanSlideBefore: overscanBefore,
            "data-test-id": "carousel"
        });
    };
    var renderButtons = function() {
        if (props.useHorizontalScrollCarousel || slideCount <= 1) {
            return null;
        }
        var isFirstSlide = slideIndex === 0;
        var isLastSlide = slideIndex === slideCount - 1;
        var _props_shouldShowArrowsOnMobile, _props_shouldShowArrowsOnMobile1;
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                props.noWrap && isFirstSlide ? null : /*#__PURE__*/ _jsx(PreviousButton, {
                    onClick: showPreviousTiles,
                    "data-test-id": "btn-previous",
                    "aria-label": "Previous Carousel Slide",
                    $offsetTop: props.buttonsOffsetTop,
                    $offsetX: props.buttonsOffsetX,
                    $showOnMobile: (_props_shouldShowArrowsOnMobile = props.shouldShowArrowsOnMobile) !== null && _props_shouldShowArrowsOnMobile !== void 0 ? _props_shouldShowArrowsOnMobile : false,
                    children: /*#__PURE__*/ _jsx(ButtonWrapper, {
                        children: /*#__PURE__*/ _jsx(Chevron, {
                            direction: "left",
                            color: props.buttonsColor
                        })
                    })
                }),
                props.noWrap && isLastSlide ? null : /*#__PURE__*/ _jsx(NextButton, {
                    onClick: showNextTiles,
                    "data-test-id": "btn-next",
                    "aria-label": "Next Carousel Slide",
                    $offsetTop: props.buttonsOffsetTop,
                    $offsetX: props.buttonsOffsetX,
                    $showOnMobile: (_props_shouldShowArrowsOnMobile1 = props.shouldShowArrowsOnMobile) !== null && _props_shouldShowArrowsOnMobile1 !== void 0 ? _props_shouldShowArrowsOnMobile1 : false,
                    children: /*#__PURE__*/ _jsx(ButtonWrapper, {
                        children: /*#__PURE__*/ _jsx(Chevron, {
                            direction: "right",
                            color: props.buttonsColor
                        })
                    })
                })
            ]
        });
    };
    if (isNaN(slideCount) || !slideCount) {
        return null;
    }
    var _props_dotsOffsetY;
    return /*#__PURE__*/ _jsxs(Wrapper, {
        ref: viewRef,
        className: props.className,
        children: [
            renderCarousel(),
            renderButtons(),
            props.shouldShowDots ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: (_props_dotsOffsetY = props.dotsOffsetY) !== null && _props_dotsOffsetY !== void 0 ? _props_dotsOffsetY : "5x"
                    }),
                    /*#__PURE__*/ _jsx(CarouselDots, {
                        onClick: function(index) {
                            handleChangeSlideIndexFromDot(index);
                        },
                        currentImageIndex: positiveMod(slideIndex, slideCount),
                        numValidImages: slideCount,
                        dotSize: "large",
                        colorVariant: props.dotsColorVariant
                    })
                ]
            }) : null
        ]
    });
};
var positiveMod = function(dividend, divisor) {
    return (dividend % divisor + divisor) % divisor;
};
var roundDown = function(value, toMultipleOf) {
    return value - positiveMod(value, toMultipleOf);
};
export var ButtonWrapper = styled.div.withConfig({
    componentId: "sc-623fb197-0"
})([
    ""
]);
/**
 * @deprecated use @faire/web/slate-labs/Carousel
 */ export var Carousel = function(props) {
    var isSSR = useIsSSR();
    var isMobile = useViewport().isMobile;
    return /*#__PURE__*/ _jsx(CarouselBase, _object_spread_props(_object_spread({}, props), {
        isSSR: isSSR,
        isMobile: isMobile
    }));
};
var Wrapper = styled.div.withConfig({
    componentId: "sc-623fb197-1"
})([
    "width:100%;position:relative;"
]);
var PaddedItem = styled.div.withConfig({
    componentId: "sc-623fb197-2"
})([
    ""
]);
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/IconButton/IconButton#IconButton"
 */ var Button = styled.button.withConfig({
    componentId: "sc-623fb197-3"
})([
    "position:absolute;top:",
    ";",
    " padding:4px 0;border-radius:8px;& > div{transition:transform 0.4s ease;}"
], function(props) {
    var _props_$offsetTop;
    return (_props_$offsetTop = props.$offsetTop) !== null && _props_$offsetTop !== void 0 ? _props_$offsetTop : "50%";
}, AccessibleOutlineCSS);
export var PreviousButton = styled(Button).withConfig({
    componentId: "sc-623fb197-4"
})([
    "left:0;transform:translate( calc((100% + ",
    ") * -1),-50% );&:hover > div{transform:translateX(-4px);}",
    ""
], function(props) {
    var _props_$offsetX;
    return (_props_$offsetX = props.$offsetX) !== null && _props_$offsetX !== void 0 ? _props_$offsetX : "16px";
}, function(param) {
    var $showOnMobile = param.$showOnMobile;
    return !$showOnMobile && css([
        "",
        "{display:none;}"
    ], responsiveMediaQueries.mobile);
});
export var NextButton = styled(Button).withConfig({
    componentId: "sc-623fb197-5"
})([
    "right:0;transform:translate( calc(100% + ",
    "),-50% );&:hover > div{transform:translateX(4px);}",
    ""
], function(props) {
    var _props_$offsetX;
    return (_props_$offsetX = props.$offsetX) !== null && _props_$offsetX !== void 0 ? _props_$offsetX : "16px";
}, function(param) {
    var $showOnMobile = param.$showOnMobile;
    return !$showOnMobile && css([
        "",
        "{display:none;}"
    ], responsiveMediaQueries.mobile);
});
var StyledSwipeableViews = styled(SwipeableViews).withConfig({
    componentId: "sc-623fb197-6"
})([
    "width:100%;",
    ""
], function(param) {
    var $slideCount = param.$slideCount, $swipeablePadding = param.$swipeablePadding, $alwaysIncludePeek = param.$alwaysIncludePeek;
    return $slideCount > 1 || $alwaysIncludePeek ? css([
        "padding:",
        ";"
    ], $swipeablePadding) : "";
});
var StyledVirtualizedSwipeableViews = styled(VirtualizedSwipeableViews).withConfig({
    componentId: "sc-623fb197-7"
})([
    "width:100%;overflow-x:clip !important;",
    ""
], function(param) {
    var $swipeablePadding = param.$swipeablePadding;
    return $swipeablePadding ? css([
        "padding:",
        ";"
    ], $swipeablePadding) : "";
});
var StyledGrid = styled(Grid).attrs({
    columnTemplate: function(c) {
        return "repeat(".concat(c, ", minmax(0, 1fr));");
    }
}).withConfig({
    componentId: "sc-623fb197-8"
})([
    "height:100%;",
    ""
], function(props) {
    return props.$padding ? css([
        "padding:",
        ";"
    ], props.$padding) : "";
});
// eslint-disable-next-line @faire/no-re-exports
export var CarouselHorizontalScrollCarouselItem = HorizontalScrollCarousel.Item;
